import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, UserPlus, Users, Euro, Calendar, Phone, Mail, FileText, Tag, X, Edit2, Trash2, Download, ChevronRight, Waves, GraduationCap, Upload, Check, Undo2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import PaymentSplitField from '../components/PaymentSplitField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = {
  vip: 'bg-amber-100 text-amber-700',
  regular: 'bg-blue-100 text-blue-700',
  familia: 'bg-purple-100 text-purple-700',
  grupo: 'bg-green-100 text-green-700',
};
const ALL_TAGS = ['vip', 'regular', 'familia', 'grupo'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtMoney = (n) => `${(n || 0).toFixed(2)}€`;

function TagBadge({ tag }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600'}`}>
      {tag}
    </span>
  );
}

function CustomerForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', dni: '', notes: '', tags: [],
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleTag = (tag) => {
    set('tags', form.tags.includes(tag) ? form.tags.filter(t => t !== tag) : [...form.tags, tag]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('El nombre es obligatorio');
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-1">Nombre *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} required
            className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-1">Teléfono</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)}
            className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-1">Email</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
            className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-1">DNI / Pasaporte</label>
          <input value={form.dni} onChange={e => set('dni', e.target.value)}
            className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-1">Notas</label>
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
          className="w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30 resize-none" />
      </div>
      <div>
        <label className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider block mb-2">Etiquetas</label>
        <div className="flex flex-wrap gap-2">
          {ALL_TAGS.map(tag => (
            <button key={tag} type="button" onClick={() => toggleTag(tag)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${form.tags.includes(tag) ? `${TAG_COLORS[tag]} border-transparent` : 'border-[#E5E0D8] text-[#8A9BA8] hover:border-[#4A6D7C]/40'}`}>
              {tag}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={saving} className="flex-1 bg-[#4A6D7C] hover:bg-[#3A5663] text-white">
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
}

function PayRow({ type, r, payAmounts, setPayAmounts, paySplits, setPaySplits, submitting, onPay }) {
  const { bizumRecipients } = useAuth();
  const [payMethod, setPayMethod] = useState(r.payment_method || 'cash');
  if ((r.amount_pending || 0) <= 0) return null;
  const id = r.id;
  const amount = payAmounts[id] || '';
  const splits = paySplits[id] || null;
  return (
    <div className="mt-2 pt-2 border-t border-[#E5E0D8] space-y-1.5">
      <div className="flex items-center gap-2">
        <Euro className="w-3.5 h-3.5 text-[#8A9BA8] shrink-0" />
        <input
          type="number"
          min="0.01"
          step="0.01"
          max={r.amount_pending}
          placeholder={`Máx. ${r.amount_pending}`}
          value={amount}
          disabled={splits?.length > 1}
          onChange={e => setPayAmounts(p => ({ ...p, [id]: e.target.value }))}
          className="flex-1 text-xs border border-[#E5E0D8] rounded px-2 py-1 focus:outline-none focus:border-[#4A6D7C] bg-white disabled:opacity-60"
        />
        <button
          onClick={() => onPay(type, id, payMethod, r.payment_method, splits)}
          disabled={submitting === id}
          className="shrink-0 bg-[#4A6D7C] hover:bg-[#3A5663] disabled:opacity-50 text-white rounded px-2 py-1 flex items-center gap-1 text-xs transition-colors"
        >
          <Check className="w-3 h-3" />
          {submitting === id ? '...' : 'Cobrar'}
        </button>
      </div>
      <select
        value={payMethod}
        onChange={e => setPayMethod(e.target.value)}
        disabled={splits?.length > 1}
        className="w-full text-xs border border-[#E5E0D8] rounded px-2 py-1 focus:outline-none focus:border-[#4A6D7C] bg-white text-[#4A5D6C] disabled:opacity-60"
      >
        {(bizumRecipients || []).map(rec => (
          <option key={rec.id} value={`bizum_${rec.id}`}>Bizum {rec.name}</option>
        ))}
        <option value="caixa">Caixa</option>
        <option value="cash">Efectivo/Cash</option>
      </select>
      <PaymentSplitField
        paymentMethod={payMethod}
        paymentSplits={splits}
        amountPaid={amount}
        bizumRecipients={bizumRecipients}
        onChange={patch => {
          if ('payment_method' in patch) setPayMethod(patch.payment_method);
          if ('payment_splits' in patch) setPaySplits(p => ({ ...p, [id]: patch.payment_splits }));
          if ('amount_paid' in patch) setPayAmounts(p => ({ ...p, [id]: patch.amount_paid }));
        }}
      />
    </div>
  );
}

function CustomerDetail({ customer, onEdit, onDelete, onClose, readOnly, maxDate, onPaymentMade, setLastPayment }) {
  const LOC = { san_antolin: 'San Antolín', san_martin: 'San Martín', ria_niembro: 'Ría de Niembro' };
  const [payAmounts, setPayAmounts] = useState({});
  const [paySplits, setPaySplits] = useState({});
  const [submitting, setSubmitting] = useState(null);

  const todayISO = new Date().toISOString().slice(0, 10);
  const sortHistory = (items, getDate) => {
    const pending = items.filter(r => (r.amount_pending || 0) > 0).sort((a, b) => (getDate(a) || '').localeCompare(getDate(b) || ''));
    const paid = items.filter(r => (r.amount_pending || 0) <= 0).sort((a, b) => (getDate(b) || '').localeCompare(getDate(a) || ''));
    return [...pending, ...paid];
  };
  const rawReservations = maxDate
    ? (customer.reservations || []).filter(r => {
        const d = r.course?.date || r.created_at?.slice(0, 10);
        if (!d || d > maxDate) return false;
        if (d >= todayISO) return true;
        return (r.amount_pending || 0) > 0;
      })
    : (customer.reservations || []);
  const reservations = sortHistory(rawReservations, r => r.course?.date || r.created_at?.slice(0, 10));
  const rawRentals = maxDate
    ? (customer.rentals || []).filter(r => {
        const d = r.date;
        if (!d || d > maxDate) return false;
        if (d >= todayISO) return true;
        return (r.amount_pending || 0) > 0;
      })
    : (customer.rentals || []);
  const rentals = sortHistory(rawRentals, r => r.date);

  const handlePay = async (type, id, paymentMethod, prevPaymentMethod, splits) => {
    const amount = parseFloat(payAmounts[id] || '');
    if (!amount || amount <= 0) { toast.error('Introduce una cantidad válida'); return; }
    if (splits && splits.some(s => !s.method || !(parseFloat(s.amount) > 0))) { toast.error('Completa ambos métodos del pago dividido con un importe mayor que 0'); return; }
    setSubmitting(id);
    try {
      const endpoint = type === 'reservation'
        ? `${API}/reservations/${id}/pay`
        : `${API}/rentals/${id}/pay`;
      await axios.patch(endpoint, { amount, payment_method: paymentMethod, payment_splits: splits }, { withCredentials: true });
      toast.success('Pago registrado');
      setLastPayment({ type, id, amount, prevPm: prevPaymentMethod || paymentMethod, splits });
      setPayAmounts(p => { const n = { ...p }; delete n[id]; return n; });
      setPaySplits(p => { const n = { ...p }; delete n[id]; return n; });
      onPaymentMade?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-[#E5E0D8]">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#4A6D7C]/10 flex items-center justify-center">
            <span className="text-2xl font-bold text-[#4A6D7C]">{customer.name.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>{customer.name}</h2>
            <div className="flex flex-wrap gap-1 mt-1">
              {(customer.tags || []).map(t => <TagBadge key={t} tag={t} />)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!readOnly && <button onClick={onEdit} className="p-2 rounded-lg text-[#4A6D7C] hover:bg-[#4A6D7C]/10 transition-colors"><Edit2 className="w-4 h-4" /></button>}
          {!readOnly && <button onClick={onDelete} className="p-2 rounded-lg text-[#E07A5F] hover:bg-[#E07A5F]/10 transition-colors"><Trash2 className="w-4 h-4" /></button>}
          <button onClick={onClose} className="p-2 rounded-lg text-[#8A9BA8] hover:bg-[#F5F2ED] transition-colors"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stats */}
      {!readOnly && (
        <div className="grid grid-cols-3 gap-3 p-4 border-b border-[#E5E0D8]">
          {[
            { label: 'Visitas', value: customer.total_visits || 0, icon: Calendar },
            { label: 'Total gastado', value: fmtMoney(customer.total_spent), icon: Euro },
            { label: 'Última visita', value: fmtDate(customer.last_visit), icon: Calendar },
          ].map(s => (
            <div key={s.label} className="bg-[#F5F2ED] rounded-xl p-3 text-center">
              <p className="text-xs text-[#8A9BA8] mb-1">{s.label}</p>
              <p className="text-sm font-bold text-[#1A2B3C]">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Contact info */}
      <div className="p-4 border-b border-[#E5E0D8] space-y-2">
        {customer.phone && <div className="flex items-center gap-2 text-sm text-[#4A5D6C]"><Phone className="w-4 h-4 text-[#8A9BA8]" />{customer.phone}</div>}
        {customer.email && <div className="flex items-center gap-2 text-sm text-[#4A5D6C]"><Mail className="w-4 h-4 text-[#8A9BA8]" />{customer.email}</div>}
        {customer.dni && <div className="flex items-center gap-2 text-sm text-[#4A5D6C]"><FileText className="w-4 h-4 text-[#8A9BA8]" />{customer.dni}</div>}
        {customer.notes && <div className="flex items-start gap-2 text-sm text-[#4A5D6C]"><FileText className="w-4 h-4 text-[#8A9BA8] mt-0.5 shrink-0" /><span className="italic">{customer.notes}</span></div>}
      </div>

      {/* History */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Reservations */}
        {reservations.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider mb-2 flex items-center gap-1">
              <GraduationCap className="w-3.5 h-3.5" /> Cursos ({reservations.length})
            </h3>
            <div className="space-y-2">
              {reservations.map((r, i) => (
                <div key={r.id || i} className="bg-[#F5F2ED] rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#1A2B3C]">
                      {r.course?.date ? fmtDate(r.course.date) : fmtDate(r.created_at)}
                      {r.course?.location && <span className="text-[#8A9BA8] ml-1">· {LOC[r.course.location] || r.course.location}</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      {r.amount_pending > 0 && <span className="text-xs font-semibold text-[#E07A5F]">Pendiente: {fmtMoney(r.amount_pending)}</span>}
                      {!readOnly && <span className="font-semibold text-[#4A6D7C]">{fmtMoney(r.amount_paid)}</span>}
                    </div>
                  </div>
                  {r.num_students > 0 && <p className="text-xs text-[#8A9BA8] mt-0.5">{r.num_students} alumno{r.num_students !== 1 ? 's' : ''}</p>}
                  <PayRow type="reservation" r={r} payAmounts={payAmounts} setPayAmounts={setPayAmounts} paySplits={paySplits} setPaySplits={setPaySplits} submitting={submitting} onPay={handlePay} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rentals */}
        {rentals.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-[#8A9BA8] uppercase tracking-wider mb-2 flex items-center gap-1">
              <Waves className="w-3.5 h-3.5" /> Alquileres ({rentals.length})
            </h3>
            <div className="space-y-2">
              {rentals.map((r, i) => (
                <div key={r.id || i} className="bg-[#F5F2ED] rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#1A2B3C]">
                      {fmtDate(r.date)}
                      <span className="text-[#8A9BA8] ml-1">· {LOC[r.location] || r.location}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {r.amount_pending > 0 && <span className="text-xs font-semibold text-[#E07A5F]">Pendiente: {fmtMoney(r.amount_pending)}</span>}
                      {!readOnly && <span className="font-semibold text-[#4A6D7C]">{fmtMoney(r.amount_paid)}</span>}
                    </div>
                  </div>
                  {r.items?.length > 0 && <p className="text-xs text-[#8A9BA8] mt-0.5">{r.items.map(it => it.type).join(', ')}</p>}
                  <PayRow type="rental" r={r} payAmounts={payAmounts} setPayAmounts={setPayAmounts} paySplits={paySplits} setPaySplits={setPaySplits} submitting={submitting} onPay={handlePay} />
                </div>
              ))}
            </div>
          </div>
        )}

        {!reservations.length && !rentals.length && (
          <p className="text-sm text-[#8A9BA8] text-center py-8">Sin historial todavía</p>
        )}
      </div>
    </div>
  );
}

export default function CRMPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortTab, setSortTab] = useState('pendiente');
  const [selected, setSelected] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [importing, setImporting] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const searchTimeout = useRef(null);

  const isCaseta = user?.role === 'caseta';

  const fetchCustomers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = { q };
      if (isCaseta) params.days_ahead = 2;
      const { data } = await axios.get(`${API}/customers`, { params, withCredentials: true });
      setCustomers(data);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  }, [isCaseta]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchCustomers(val), 350);
  };

  const loadDetail = async (customer) => {
    if (selected?.id !== customer.id) setLastPayment(null);
    setSelected(customer);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/customers/${customer.id}`, { withCredentials: true });
      setDetailData(data);
    } catch { toast.error('Error al cargar detalle'); }
    finally { setDetailLoading(false); }
  };

  const handleUndo = async () => {
    if (!lastPayment) return;
    const { type, id, amount, prevPm, splits } = lastPayment;
    try {
      const endpoint = type === 'reservation'
        ? `${API}/reservations/${id}/unpay`
        : `${API}/rentals/${id}/unpay`;
      await axios.patch(endpoint, { amount, payment_method: prevPm, payment_splits: splits }, { withCredentials: true });
      toast.success('Cobro deshecho');
      setLastPayment(null);
      if (selected) { loadDetail(selected); fetchCustomers(search); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al deshacer');
    }
  };

  const handleSave = async (form) => {
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, form, { withCredentials: true });
        toast.success('Cliente actualizado');
        if (detailData?.id === editingCustomer.id) {
          setDetailData(d => ({ ...d, ...form }));
        }
      } else {
        await axios.post(`${API}/customers`, form, { withCredentials: true });
        toast.success('Cliente creado');
      }
      setShowForm(false);
      setEditingCustomer(null);
      fetchCustomers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`¿Eliminar a ${customer.name}? Solo se elimina el perfil, no su historial.`)) return;
    try {
      await axios.delete(`${API}/customers/${customer.id}`, { withCredentials: true });
      toast.success('Cliente eliminado');
      if (selected?.id === customer.id) { setSelected(null); setDetailData(null); }
      fetchCustomers(search);
    } catch { toast.error('Error al eliminar'); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/customers/import`, {}, { withCredentials: true });
      const parts = [];
      if (data.created) parts.push(`${data.created} creados`);
      if (data.updated) parts.push(`${data.updated} actualizados`);
      if (data.deleted) parts.push(`${data.deleted} eliminados`);
      toast.success(parts.length ? parts.join(', ') : 'Sin cambios nuevos');
      fetchCustomers(search);
    } catch { toast.error('Error al importar'); }
    finally { setImporting(false); }
  };

  return (
    <div className="flex h-full min-h-0 gap-0">
      {/* Left panel */}
      <div className={`flex flex-col ${selected ? 'hidden lg:flex lg:w-96' : 'w-full'} border-r border-[#E5E0D8] bg-white`}>
        {/* Header */}
        <div className="p-4 border-b border-[#E5E0D8] space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Clientes</h1>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleImport} disabled={importing} title="Importar desde reservas existentes">
                <Upload className="w-4 h-4 mr-1" /><span className="hidden sm:inline">{importing ? 'Importando...' : 'Importar'}</span>
              </Button>
              <Button size="sm" className="bg-[#4A6D7C] hover:bg-[#3A5663] text-white"
                onClick={() => { setEditingCustomer(null); setShowForm(true); }}>
                <UserPlus className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Nuevo</span>
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9BA8]" />
            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Buscar por nombre, teléfono o email..."
              className="w-full border border-[#E5E0D8] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]/30" />
          </div>
          <div className="flex border border-[#E5E0D8] rounded-lg overflow-hidden text-xs font-semibold">
            <button onClick={() => setSortTab('pendiente')}
              className={`flex-1 py-1.5 transition-colors ${sortTab === 'pendiente' ? 'bg-[#E07A5F] text-white' : 'text-[#8A9BA8] hover:bg-[#F5F2ED]'}`}>
              Pendiente
            </button>
            {!isCaseta && (
              <button onClick={() => setSortTab('spent')}
                className={`flex-1 py-1.5 transition-colors border-l border-[#E5E0D8] ${sortTab === 'spent' ? 'bg-[#4A6D7C] text-white' : 'text-[#8A9BA8] hover:bg-[#F5F2ED]'}`}>
                Mayor gasto
              </button>
            )}
            <button onClick={() => setSortTab('alpha')}
              className={`flex-1 py-1.5 transition-colors border-l border-[#E5E0D8] ${sortTab === 'alpha' ? 'bg-[#4A6D7C] text-white' : 'text-[#8A9BA8] hover:bg-[#F5F2ED]'}`}>
              A–Z
            </button>
          </div>
        </div>

        {/* Form inline */}
        {showForm && (
          <div className="p-4 border-b border-[#E5E0D8] bg-[#FAFAF8]">
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">{editingCustomer ? 'Editar cliente' : 'Nuevo cliente'}</h3>
            <CustomerForm
              initial={editingCustomer || {}}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditingCustomer(null); }}
            />
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-[#8A9BA8] text-sm">Cargando...</div>
          ) : customers.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <Users className="w-10 h-10 text-[#C5BFB8] mx-auto" />
              <p className="text-sm text-[#8A9BA8]">{search ? 'Sin resultados' : 'Todavía no hay clientes'}</p>
              {!search && <Button size="sm" variant="outline" onClick={handleImport} disabled={importing}><Upload className="w-4 h-4 mr-1" />Importar desde reservas</Button>}
            </div>
          ) : (
            <div className="divide-y divide-[#F5F2ED]">
              {(sortTab === 'spent'
                ? [...customers].sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
                : sortTab === 'pendiente'
                ? [...customers].filter(c => (c.total_pending || 0) > 0).sort((a, b) => (a.pending_since || '').localeCompare(b.pending_since || ''))
                : customers
              ).map(c => (
                <button key={c.id} onClick={() => loadDetail(c)}
                  className={`w-full text-left px-4 py-3.5 hover:bg-[#FAFAF8] transition-colors flex items-center gap-3 ${selected?.id === c.id ? 'bg-[#4A6D7C]/5 border-l-2 border-[#4A6D7C]' : ''}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#4A6D7C]/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-[#4A6D7C]">{c.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-[#1A2B3C] truncate">{c.name}</p>
                      {!isCaseta && <span className="text-xs font-medium text-[#4A6D7C] shrink-0">{fmtMoney(c.total_spent)}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p className="text-xs text-[#8A9BA8] truncate">{c.phone || c.email || 'Sin contacto'}</p>
                      {!isCaseta && <span className="text-xs text-[#8A9BA8] shrink-0">{c.total_visits} visita{c.total_visits !== 1 ? 's' : ''}</span>}
                    </div>
                    {c.total_pending > 0 && !(isCaseta && sortTab === 'alpha') && (
                      <div className="mt-0.5">
                        <span className="text-xs font-semibold text-[#E07A5F]">Pendiente: {fmtMoney(c.total_pending)}</span>
                      </div>
                    )}
                    {(c.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.tags.map(t => <TagBadge key={t} tag={t} />)}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#C5BFB8] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer count */}
        {customers.length > 0 && (
          <div className="px-4 py-2 border-t border-[#E5E0D8] text-xs text-[#8A9BA8]">
            {customers.length} cliente{customers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Right panel — detail */}
      {selected && (
        <div className="flex-1 min-w-0 bg-white overflow-hidden flex flex-col">
          {lastPayment && (
            <div className="px-4 pt-3 shrink-0">
              <button
                onClick={handleUndo}
                className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-[#E07A5F] text-[#E07A5F] text-xs font-medium hover:bg-[#E07A5F]/10 transition-colors"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Deshacer último cobro ({lastPayment.amount.toFixed(2)} €)
              </button>
            </div>
          )}
          {detailLoading && !detailData && (
            <div className="flex-1 flex items-center justify-center text-[#8A9BA8] text-sm">Cargando...</div>
          )}
          {detailData && (
            <CustomerDetail
              key={selected.id}
              customer={detailData}
              onEdit={() => { setEditingCustomer(detailData); setShowForm(true); setSelected(null); setDetailData(null); }}
              onDelete={() => handleDelete(detailData)}
              onClose={() => { setSelected(null); setDetailData(null); setLastPayment(null); }}
              readOnly={isCaseta}
              maxDate={isCaseta ? (() => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10); })() : undefined}
              onPaymentMade={() => { loadDetail(selected); fetchCustomers(search); }}
              setLastPayment={setLastPayment}
            />
          )}
        </div>
      )}
    </div>
  );
}
